import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  TablePagination,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useQAData } from '../hooks/useQAData';
import { formatDate, getRatingIcon, cleanQuestionNumber, cleanQuestionText } from '../utils/qaHelpers';
import { PageLayout } from '../../../shared/components';
import AnswerSection from './AnswerSection';
import { QuestionEnglishInline } from './QuestionEnglishInline';
import './ReviewAnswers.css';

const ViewClientQuestions: React.FC = () => {
  const {
    qaItems,
    loading,
    error,
    page,
    rowsPerPage,
    total,
    activeFilter,
    setError,
    handleChangePage,
    handleChangeRowsPerPage,
    handleFilterClick,
    handleResetAll,
  } = useQAData(['liked', 'edited', 'rejected'], false, true); // Don't exclude admin statuses; always show only current user's questions

  return (
    <PageLayout>
      {/* Title and Instructions */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: '2rem',
              color: '#333',
              mb: 1.5,
              fontFamily: '"Bitter", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            }}
          >
            View your questions and answers
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#666',
              fontSize: '1rem',
              mb: 0.5,
            }}
          >
            View all your uploaded questions and answers. Copy recently generated answers.
          </Typography>
        </Box>

        {/* Filter Buttons - Only Liked, Edited, Disliked */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ThumbUpIcon />}
            onClick={() => handleFilterClick('liked')}
            sx={{
              minHeight: '36px',
              px: 2,
              border: activeFilter === 'liked' ? '1.5px solid #156C86' : '1px solid #d9dede',
              backgroundColor: activeFilter === 'liked' ? 'rgba(71, 161, 173, 0.1)' : '#ffffff',
              color: activeFilter === 'liked' ? '#156C86' : '#333333',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: '40px',
              boxShadow: 'none',
              '& .MuiButton-startIcon': {
                color: activeFilter === 'liked' ? '#47A1AD' : '#999999',
              },
              '&:hover': {
                border: activeFilter === 'liked' ? '1.5px solid #156C86' : '1px solid #666666',
                backgroundColor: activeFilter === 'liked' ? 'rgba(71, 161, 173, 0.1)' : 'rgba(71, 161, 173, 0.1)',
                color: activeFilter === 'liked' ? '#156C86' : '#212121',
                boxShadow: 'none',
                '& .MuiButton-startIcon': {
                  color: activeFilter === 'liked' ? '#47A1AD' : '#666666',
                },
              },
            }}
          >
            Liked
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => handleFilterClick('edited')}
            sx={{
              minHeight: '36px',
              px: 2,
              border: activeFilter === 'edited' ? '1.5px solid #156C86' : '1px solid #d9dede',
              backgroundColor: activeFilter === 'edited' ? 'rgba(71, 161, 173, 0.1)' : '#ffffff',
              color: activeFilter === 'edited' ? '#156C86' : '#333333',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: '40px',
              boxShadow: 'none',
              '& .MuiButton-startIcon': {
                color: activeFilter === 'edited' ? '#47A1AD' : '#999999',
              },
              '&:hover': {
                border: activeFilter === 'edited' ? '1.5px solid #156C86' : '1px solid #666666',
                backgroundColor: activeFilter === 'edited' ? 'rgba(71, 161, 173, 0.1)' : 'rgba(71, 161, 173, 0.1)',
                color: activeFilter === 'edited' ? '#156C86' : '#212121',
                boxShadow: 'none',
                '& .MuiButton-startIcon': {
                  color: activeFilter === 'edited' ? '#47A1AD' : '#666666',
                },
              },
            }}
          >
            Edited
          </Button>
          <Button
            variant="outlined"
            startIcon={<ThumbDownIcon />}
            onClick={() => handleFilterClick('rejected')}
            sx={{
              minHeight: '36px',
              px: 2,
              border: activeFilter === 'rejected' ? '1.5px solid #C42E30' : '1px solid #d9dede',
              backgroundColor: activeFilter === 'rejected' ? 'rgba(196, 46, 48, 0.1)' : '#ffffff',
              color: activeFilter === 'rejected' ? '#C42E30' : '#333333',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: '40px',
              boxShadow: 'none',
              '& .MuiButton-startIcon': {
                color: activeFilter === 'rejected' ? '#C42E30' : '#999999',
              },
              '&:hover': {
                border: activeFilter === 'rejected' ? '1.5px solid #C42E30' : '1px solid #666666',
                backgroundColor: activeFilter === 'rejected' ? 'rgba(196, 46, 48, 0.1)' : 'rgba(196, 46, 48, 0.1)',
                color: activeFilter === 'rejected' ? '#C42E30' : '#212121',
                boxShadow: 'none',
                '& .MuiButton-startIcon': {
                  color: activeFilter === 'rejected' ? '#C42E30' : '#666666',
                },
              },
            }}
          >
            Disliked
          </Button>
          <Button
            variant="text"
            onClick={handleResetAll}
            sx={{
              minHeight: '36px',
              px: 1,
              color: '#333333',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: '40px',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
            }}
          >
            Reset all
          </Button>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontSize: '0.95rem',
              },
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Questions List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : qaItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body2" color="textSecondary">
              No questions found
            </Typography>
          </Box>
        ) : (
          <>
            {/* Top Pagination */}
            <Paper
              elevation={0}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 0,
                backgroundColor: '#ffffff',
                mb: 2,
              }}
            >
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    px: 2,
                    py: 1,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            </Paper>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              {qaItems.map((item) => (
                <Card
                  key={item.id}
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    {/* Header with metadata */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      mb: 2,
                      pb: 1.5,
                      borderBottom: '1px solid #e0e0e0',
                    }}>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Chip
                          label={formatDate(item.question_created_at)}
                          size="small"
                          className="common-chip"
                          sx={{
                            backgroundColor: '#f5f5f5',
                            color: '#666666',
                          }}
                        />
                        {item.client_name && (
                          <Chip
                            label={item.client_name}
                            size="small"
                            className="common-chip"
                            sx={{
                              backgroundColor: '#f5f5f5',
                              color: '#666666',
                            }}
                          />
                        )}
                        {item.user_email && (
                          <Chip
                            label={item.user_email}
                            size="small"
                            className="common-chip"
                            sx={{
                              backgroundColor: '#f5f5f5',
                              color: '#666666',
                            }}
                          />
                        )}
                        {item.feedback_rating && getRatingIcon(item.feedback_rating) && (() => {
                          const normalizedRating = item.feedback_rating.toUpperCase();
                          const isLiked = normalizedRating === 'LIKED' || normalizedRating === 'POSITIVE';
                          const isDisliked = normalizedRating === 'DISLIKED' || normalizedRating === 'NEGATIVE';
                          const label = isLiked ? 'Liked' : isDisliked ? 'Disliked' : item.feedback_rating.charAt(0).toUpperCase() + item.feedback_rating.slice(1).toLowerCase();
                          
                          return (
                            <Chip
                              icon={getRatingIcon(item.feedback_rating) || undefined}
                              label={label}
                              size="small"
                              className="common-chip"
                              variant="filled"
                              sx={{
                                backgroundColor: isLiked ? 'rgba(71, 161, 173, 0.1)' : 'rgba(196, 46, 48, 0.1)',
                                color: isLiked ? '#156C86' : '#C42E30',
                                border: 'none',
                                '& .MuiChip-icon': {
                                  color: isLiked ? '#47A1AD' : '#C42E30',
                                },
                              }}
                            />
                          );
                        })()}
                        {item.admin_status && (() => {
                          const status = item.admin_status.toUpperCase();
                          let chipColor = '#666666';
                          let chipBg = '#f5f5f5';
                          let label = item.admin_status;
                          
                          if (status === 'APPROVED') {
                            chipColor = '#6B9E78';
                            chipBg = 'rgba(107, 158, 120, 0.1)';
                            label = 'Approved';
                          } else if (status === 'REVIEWED') {
                            chipColor = '#156C86';
                            chipBg = 'rgba(71, 161, 173, 0.1)';
                            label = 'Reviewed';
                          } else if (status === 'ARCHIVED') {
                            chipColor = '#156C86';
                            chipBg = 'rgba(71, 161, 173, 0.1)';
                            label = 'Archived';
                          }
                          
                          return (
                            <Chip
                              label={label}
                              size="small"
                              className="common-chip"
                              variant="filled"
                              sx={{
                                backgroundColor: chipBg,
                                color: chipColor,
                                border: 'none',
                              }}
                            />
                          );
                        })()}
                      </Box>
                    </Box>

                    {/* Question and Answers Accordion */}
                    <Accordion
                      defaultExpanded={false}
                      sx={{
                        boxShadow: 'none',
                        border: 'none',
                        '&:before': {
                          display: 'none',
                        },
                        '&.Mui-expanded': {
                          margin: 0,
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: '#48a1ad' }} />}
                        sx={{
                          px: 0,
                          '&.Mui-expanded': {
                            minHeight: '48px',
                          },
                          '& .MuiAccordionSummary-content': {
                            margin: 0,
                            '&.Mui-expanded': {
                              margin: 0,
                            },
                          },
                        }}
                      >
                        <QuestionEnglishInline
                          questionLocale={item.question_locale}
                          accent="legacy"
                          sx={{ width: '100%', pr: 1 }}
                          originalNode={
                            <Typography
                              variant="h6"
                              sx={{
                                color: '#48a1ad',
                                fontWeight: 600,
                                fontSize: '1.1rem',
                                lineHeight: 1.5,
                              }}
                            >
                              {cleanQuestionNumber(item.question_number)}.{' '}
                              {cleanQuestionText(item.full_question)}
                            </Typography>
                          }
                          englishNode={
                            item.full_question_english ? (
                              <Typography
                                variant="h6"
                                sx={{
                                  color: '#48a1ad',
                                  fontWeight: 600,
                                  fontSize: '1.1rem',
                                  lineHeight: 1.5,
                                }}
                              >
                                {item.full_question_english}
                              </Typography>
                            ) : null
                          }
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pt: 0 }}>
                        <AnswerSection item={item} showCopyButton={true} />
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {/* Bottom Pagination */}
            <Paper
              elevation={0}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 0,
                backgroundColor: '#ffffff',
              }}
            >
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    px: 2,
                    py: 1,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            </Paper>
          </>
        )}
    </PageLayout>
  );
};

export default ViewClientQuestions;

